import { useState } from "react";
import { ArrowRight } from "lucide-react";

import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { BRAND } from "@/lib/site";
import { useWaitlist, type WaitlistVariant } from "@/components/WaitlistContext";
import { submitWaitlist } from "@/lib/waitlist";
import { trackEvent } from "@/lib/analytics";

const COPY: Record<WaitlistVariant, { title: string; description: string; cta: string }> = {
  waitlist: {
    title: "Get early access",
    description: `Join the ${BRAND} waitlist. We'll email you when your agent is ready — no spam, just launch updates.`,
    cta: "Join waitlist",
  },
  sales: {
    title: "Talk to our team",
    description: `Leave your email and we'll reach out within 24 hours to discuss ${BRAND} for your team.`,
    cta: "Request a call",
  },
  request: {
    title: "Request an integration",
    description: "Tell us which tool matters most and we'll prioritize it.",
    cta: "Submit request",
  },
};

export function WaitlistDialog() {
  const { open, variant, meta, setOpen } = useWaitlist();
  const [email, setEmail] = useState("");
  const [message, setMessage] = useState("");
  const [status, setStatus] = useState<"idle" | "submitting" | "success" | "error">("idle");

  const copy = COPY[variant];
  const showMessage = variant === "sales" || variant === "request";

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setStatus("submitting");

    try {
      await submitWaitlist({ email, message, variant });
      trackEvent("Waitlist_Submit", { variant });
      setStatus("success");
    } catch {
      setStatus("error");
    }
  }

  function handleOpenChange(next: boolean) {
    if (!next) {
      setEmail("");
      setMessage("");
      setStatus("idle");
    }
    setOpen(next);
  }

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent className="sm:max-w-md">
        {status === "success" ? (
          <div className="py-6 text-center">
            <div className="mx-auto mb-4 inline-flex h-12 w-12 items-center justify-center rounded-full bg-coral-50 text-coral-700">
              <ArrowRight className="h-5 w-5" />
            </div>
            <DialogHeader>
              <DialogTitle>You're on the list</DialogTitle>
              <DialogDescription>
                We'll be in touch soon. Keep an eye on your inbox.
              </DialogDescription>
            </DialogHeader>
          </div>
        ) : (
          <>
            <DialogHeader>
              <DialogTitle>{copy.title}</DialogTitle>
              <DialogDescription>{copy.description}</DialogDescription>
            </DialogHeader>
            {meta.plan && (
              <p className="mt-2 text-sm font-medium text-foreground">
                Selected plan: <span className="text-coral-700">{meta.plan}</span>
              </p>
            )}
            <form onSubmit={handleSubmit} className="mt-4 space-y-4">
              <div>
                <label htmlFor="waitlist-email" className="sr-only">
                  Email address
                </label>
                <input
                  id="waitlist-email"
                  type="email"
                  required
                  placeholder="you@company.com"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="h-11 w-full rounded-md border border-input bg-background px-3 text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
/>
              </div>
              {showMessage && (
                <div>
                  <label htmlFor="waitlist-message" className="sr-only">
                    Message
                  </label>
                  <textarea
                    id="waitlist-message"
                    rows={3}
                    placeholder={
                      variant === "request"
                        ? "Which tool would you like us to integrate?"
                        : "Tell us about your team and use case"
                    }
                    value={message}
                    onChange={(e) => setMessage(e.target.value)}
                    className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
                  />
                </div>
              )}
              {status === "error" && (
                <p className="text-sm text-destructive">
                  Something went wrong. Please try again.
                </p>
              )}
              <Button type="submit" className="w-full" disabled={status === "submitting"}>
                {status === "submitting" ? "Submitting..." : copy.cta}
              </Button>
            </form>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}
